import { Injectable } from '@angular/core';

import {
  BehaviorSubject,
  Observable,
  asyncScheduler,
  combineLatest,
  forkJoin,
  of,
} from 'rxjs';
import {
  debounceTime,
  filter,
  map,
  shareReplay,
  startWith,
  switchMap,
} from 'rxjs/operators';

import {
  Entity,
  ReactiveListVM,
  ReactiveStore,
  StatusState,
} from '@dg/shared-rsm';
import { AuthService } from '@dg/shared-services';

import { TagsService } from '@app/tags/services/tags.service';
import { InputsService } from '@app/inputs/services/inputs.service';
import { AuthUser } from '@app/account/account-api.model';

import { Profile } from '@app/profile/profile.model';
import { ProfileOverviewService } from '@app/profile/components/profile-overview/profile-overview.service';
import { ProfileService } from '@app/profile/services/profile.service';
import { toRegistry } from '@app/profile/components/profile-overview-v2/profile-overview.utils';

import { EngagedLearnerService } from '@app/shared/services/engaged-learner.service';
import { TrackerService } from '@app/shared/services/tracker.service';
import { TrackingEventArgs } from '@app/shared/services/tracking.model';
import { FollowingService } from '@app/shared/services/following.service';

import {
  hasSkills,
  getEmptyTileCount,
} from '@app/profile/components/profile-overview-v2/profile-overview.utils';
import { ProfileSkillSignalServiceV2 } from '@app/profile/components/profile-skill-signals-page-v2/profile-skill-signal-page.service';

import {
  ProfileOverviewViewModel,
  ProfileOverviewState,
  ProfileOverviewAPI,
  ProfileOverviewStoreState,
} from './profile-overview.model';

import {
  ExperienceItem,
  ProfileExperiencesFacade,
} from '@app/profile/components/profile-overview/experiences-v2/profile-experience.facade';
import {
  AchievementItem,
  ProfileAchievementsFacade,
} from '@app/profile/components/profile-overview/achievements-v2/achievements.facade';
import {
  LearningItem,
  ProfileLearningFacade,
} from '@app/profile/components/profile-overview/learning-v2/profile-learning.facade';

export const EXPERIENCES_DISPLAY_LIMIT = 5;
export const INPUTS_DISPLAY_LIMIT = 3;
// *****************************************************************
// Utility functions
// *****************************************************************

const initStore = (): ProfileOverviewStoreState => ({
  collectionCounts: {
    learningsCount: 0,
    experiencesCount: 0,
    achievementsCount: 0,
  },
  hasParsedResume: false,
  signalCounts: [],
  signalHistories: [],
  historyXAxisLabels: [],
  signalSummaries: {},
  skills: { focus: [], other: [] },
  skillRatingsCount: 0,
  hasNoSkills: true,
  isLoading: false,
  showSkeleton: false,
});

/**
 * Facade for the Profile Overview
 */
@Injectable()
export class ProfileOverviewFacade {
  /**
   * Scheduler used for `debounceTime()` for the profile overview
   * This allows tests to override with a virtual scheduler
   */
  public static debounceScheduler = asyncScheduler;

  private get owner(): Profile {
    return this.profileService.profileInput.profile;
  }

  private _reload$ = new BehaviorSubject<boolean>(true);
  private _enabled = true;
  private _store: ReactiveStore<ProfileOverviewStoreState, Entity>;

  public get authUser(): AuthUser {
    return this.authService?.authUser;
  }

  public get snapshot(): ProfileOverviewStoreState {
    return this._store.snapshot;
  }

  /**
   * Special property to either force reload or IGNORE other triggers
   * Use to temporarily disable the facade when not needed
   */
  public get enabled(): boolean {
    return this._enabled;
  }

  public set enabled(value: boolean) {
    if (value === this._enabled) return;
    this._enabled = value;

    // Force trigger$ emission to refresh the profile VM
    if (!!value) this._reload$.next(true);
  }

  public status$: Observable<StatusState>;
  public vm$: Observable<ProfileOverviewViewModel>;

  public readonly experiencesSearch$: Observable<
    ReactiveListVM<ExperienceItem>
  >;

  public readonly achievementsSearch$: Observable<
    ReactiveListVM<AchievementItem>
  >;

  public readonly learningsSearch$: Observable<ReactiveListVM<LearningItem>>;

  constructor(
    private api: ProfileOverviewService,
    private tagsService: TagsService,
    private inputsService: InputsService,
    private authService: AuthService,
    private profileService: ProfileService,
    private skillSignalsService: ProfileSkillSignalServiceV2,
    private tracker: TrackerService,
    private engagedLearnerService: EngagedLearnerService,
    private followingService: FollowingService,
    private experiencesFacade: ProfileExperiencesFacade,
    private achievementsFacade: ProfileAchievementsFacade,
    private learningsFacade: ProfileLearningFacade
  ) {
    this._store = new ReactiveStore<ProfileOverviewStoreState, Entity>(
      'profileOverview',
      initStore
    );
    this.experiencesSearch$ = this.experiencesFacade.experienceItems.vm$;
    this.achievementsSearch$ = this.achievementsFacade.achievementItems.vm$;
    this.learningsSearch$ = this.learningsFacade.learningItems.vm$;

    // ViewModel is state + computed properties
    this.vm$ = combineLatest([
      this._store.state$,
      this.authService.authUser$,
    ]).pipe(
      map(this.addComputedProperties.bind(this)),
      map(this.addViewModelAPI.bind(this)),
      map(this.trackProfileView.bind(this)),
      shareReplay<ProfileOverviewViewModel>({ refCount: false, bufferSize: 1 })
    );
    this.status$ = this._store.status$;

    // Configure a permanent connection to external triggers
    // NOTE: if triggers are NOT BehaviorSubjects, may need to `startWith()` to force an initial emission
    const triggers$ = combineLatest([
      this._reload$,
      this.tagsService.userTagsModified.pipe(startWith({})),
      this.inputsService.inputModify.pipe(startWith({})),
    ]).pipe(
      filter(() => this.enabled),
      debounceTime(20, ProfileOverviewFacade.debounceScheduler)
    );

    this.updateWhen(triggers$);
  }

  // ************************************************************
  // Private Methods
  // ************************************************************

  /**
   * Establishes a permanent connection to external triggers and updates the store
   * based on queries results of 'data' providers.
   *
   * NOTE: this 'permanent connection' means we need a way to temporarily ignore trigger emissions
   *       hence the use of `enabled` for the facade
   */
  private updateWhen(triggers$: Observable<unknown>): void {
    const onSaveToStore = ({
      skillsAndIds: { skills, ratingsTotalCount },
      collectionCounts,
      overviewSettings,
      signalSummaries,
    }) => {
      const hasNoSkills = !hasSkills(skills);

      const {
        signalCounts,
        signalHistories,
        xAxisLabels: historyXAxisLabels,
      } = overviewSettings.signalCounts;

      this._store.update((state) => ({
        ...state,
        hasNoSkills,
        skills: hasNoSkills ? [] : skills,
        skillRatingsCount: ratingsTotalCount,
        hasParsedResume: overviewSettings.hasParsedResume,
        signalCounts,
        signalHistories,
        historyXAxisLabels,
        signalSummaries,
        collectionCounts,
      }));
    };

    // !! do not cache the `unsubscribe()` as this is a permanent connection
    triggers$
      .pipe(
        this._store.trackLoadStatus(),
        switchMap(this.loadSkillsAndOverview.bind(this)),
        switchMap(this.loadSummaries.bind(this))
      )
      .subscribe(onSaveToStore);
  }

  // ************************************************************
  // Private Remote data accessors
  // ************************************************************

  /**
   * Load list of overview settings
   */
  private getOverviewSettings(ownerKey: number, ownerIsViewing = false) {
    return forkJoin({
      hasParsedResume: ownerIsViewing
        ? this.api.getHasParsedResume()
        : of({ hasBeenParsed: false }),
      signalCounts: this.skillSignalsService.getSignalCounts(ownerKey),
    });
  }

  /**
   *
   */
  private loadSkillsAndOverview() {
    const isOwner = this.profileService.ownerIsViewing;
    const userKey = this.owner.userProfileKey;
    const isLoggedIn = !!this.authUser;
    const queries$ = forkJoin({
      skillsAndIds: this.api.getAllSkills(userKey),
      collectionCounts: this.skillSignalsService.getCollectionCounts(
        userKey,
        isLoggedIn
      ),
      overviewSettings: this.getOverviewSettings(userKey, isOwner),
      experiences: this.experiencesFacade.getExperiences(
        null,
        true,
        EXPERIENCES_DISPLAY_LIMIT
      ),
      achievements: this.achievementsFacade.getAchievements(
        null,
        true,
        INPUTS_DISPLAY_LIMIT
      ),
      learnings: this.learningsFacade.getLearnings(
        null,
        true,
        INPUTS_DISPLAY_LIMIT
      ),
    });

    // Update the store status just BEFORE the queries are dispatched
    return queries$.pipe(this._store.trackLoadStatus());
  }

  /**
   *
   */
  private loadSummaries(info) {
    const userKey = this.owner.userProfileKey;
    const mergeWithInfo = (signalSummaries) => ({ ...info, signalSummaries });

    return this.skillSignalsService
      .getSignalBreakdown(userKey, info.skillsAndIds.allTagIds)
      .pipe(map(toRegistry), map(mergeWithInfo));
  }

  // *******************************************************
  // ViewModel 'injection' methods
  // *******************************************************

  /**
   * Inject the Facade computed properties into view model
   */
  private addComputedProperties([state, authUser, isLoading]: [
    ProfileOverviewStoreState,
    AuthUser,
    boolean
  ]): ProfileOverviewState {
    const showExperiences = !authUser?.hideOverviewExperience;
    const showAchievements = !authUser?.hideOverviewAchievement;
    const isLoggedIn = !!authUser;
    const ownerIsViewing = this.profileService.ownerIsViewing;
    const ownerKey = this.owner.id;

    const showSidebar =
      !state.hasNoSkills && showExperiences && showAchievements;
    const showBuildYourProfile = ownerIsViewing && showSidebar;
    const showActivity = !ownerIsViewing && showSidebar;
    return {
      ...state,
      showExperiences,
      showAchievements,
      isLoggedIn,
      ownerIsViewing,
      ownerKey,
      showBuildYourProfile,
      showActivity,
    };
  }

  /**
   * Inject the Facade API into view model
   */
  private addViewModelAPI(
    state: ProfileOverviewViewModel
  ): ProfileOverviewViewModel & ProfileOverviewAPI {
    const api = {
      getEmptyTileCount,
    };

    return {
      ...state,
      ...api,
    };
  }

  // ************************************************************
  // Event Reporting
  // ************************************************************
  private trackProfileView(
    vm: ProfileOverviewViewModel,
    index: number
  ): ProfileOverviewViewModel {
    // only track on the initial load, or if a new profile is loaded
    if (index === 0 || vm.ownerKey !== this.owner.id) {
      const privacyOptions = { 0: 'Public', 1: 'OrgVisible', 2: 'Private' };
      const ownerIsViewing = this.profileService.ownerIsViewing;
      const profileOwnerId = ownerIsViewing ? 'self' : this.owner.id;
      const isFollowing = ownerIsViewing
        ? 'self'
        : this.followingService.isViewersFollowee(this.owner.guid);
      const trackProperties = {
        ProfileOwnerId: profileOwnerId,
        ProfileVisibility:
          privacyOptions[this.profileService.profileInput.options.privacyId],
        SkillsAddedCount: vm.skills.other.length,
        SkillRatingsCount: vm.skillRatingsCount,
        FocusSkillsCount: vm.skills.focus.length,
        ExperiencesCount: vm.collectionCounts.experiencesCount,
        AchievementsCount: vm.collectionCounts.achievementsCount,
        LearningsCount: vm.collectionCounts.learningsCount,
        IsActiveLearner: this.engagedLearnerService.isEnabled,
        IsFollowing: isFollowing,
        ProfileType: this.profileService.getProfileType(),
      };

      this.trackEvent('Profile Viewed', trackProperties);
    }
    return vm;
  }

  private trackEvent(
    action: string,
    properties?: { [name: string]: any }
  ): void {
    let data: TrackingEventArgs = { action };
    // add properties, if defined
    if (properties) {
      data = {
        ...data,
        properties,
      };
    }
    // track event data
    this.tracker.trackEventData(data);
  }
}
